// Go API 클라이언트
import type {
  HomeResponse,
  ExploreResponse,
  WikiProcedureDetail,
  EvidenceResponse,
  SuggestItem,
  SearchResultItem,
  CompareResponse,
} from "@/types/api";

// 서버 사이드: 절대 URL (Next.js 서버 → Go API)
// 클라이언트 사이드: 상대 경로 (브라우저 → nginx → Go API)
const API_BASE =
  typeof window === "undefined"
    ? process.env.NEXT_PUBLIC_API_URL || "http://localhost:8081/api/v1"
    : "/api/v1";

// 캐싱 전략별 fetch 옵션
const CACHE_STRATEGIES = {
  // 자주 변하지 않는 데이터: ISR (60초마다 재검증)
  isr60: { next: { revalidate: 60 } } as RequestInit,
  // 중간 빈도: ISR (5분마다 재검증)
  isr300: { next: { revalidate: 300 } } as RequestInit,
  // 거의 변하지 않는 데이터: ISR (1시간)
  isr3600: { next: { revalidate: 3600 } } as RequestInit,
  // 실시간 데이터: 캐시 없음
  noStore: { cache: "no-store" as const } as RequestInit,
} as const;

async function fetcher<T>(path: string, options?: RequestInit): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...options?.headers,
    },
  });

  if (!res.ok) {
    throw new Error(`API ${res.status}: ${res.statusText}`);
  }

  return res.json();
}

// ── 홈 ──
// 홈 데이터(태그 통계, 인기 시술)는 자주 변하지 않으므로 60초 ISR
export function getHome() {
  return fetcher<HomeResponse>("/home", CACHE_STRATEGIES.isr60);
}

// ── 탐색 ──
export function getExplore(params: {
  purpose?: string;
  method?: string;
  area?: string;
  grade?: string;
  safe?: boolean;
}) {
  const sp = new URLSearchParams();
  if (params.purpose) sp.set("purpose", params.purpose);
  if (params.method) sp.set("method", params.method);
  if (params.area) sp.set("area", params.area);
  if (params.grade) sp.set("grade", params.grade);
  if (params.safe) sp.set("safe", "1");
  const qs = sp.toString();
  // 탐색 결과는 필터 조합별로 캐시 가능, 60초 ISR
  return fetcher<ExploreResponse>(`/explore${qs ? `?${qs}` : ""}`, CACHE_STRATEGIES.isr60);
}

// ── 시술 상세 ──
// 위키 상세는 데이터가 거의 변하지 않으므로 5분 ISR
export function getWikiDetail(slug: string) {
  return fetcher<WikiProcedureDetail>(`/wiki/${encodeURIComponent(slug)}`, CACHE_STRATEGIES.isr300);
}

// ── 그룹 상세 ──
// 그룹 상세도 위키와 동일하게 5분 ISR
export function getGroupDetail(slug: string) {
  return fetcher<WikiProcedureDetail>(
    `/wiki/g/${encodeURIComponent(slug)}`,
    CACHE_STRATEGIES.isr300,
  );
}

// ── 근거 상세 ──
// 임상 근거 데이터는 거의 변하지 않으므로 1시간 ISR
export function getEvidence(slug: string) {
  return fetcher<EvidenceResponse>(
    `/evidence/${encodeURIComponent(slug)}`,
    CACHE_STRATEGIES.isr3600,
  );
}

// ── 검색/자동완성 ──
export function getSuggest(q: string) {
  return fetcher<SuggestItem[]>(`/suggest?q=${encodeURIComponent(q)}`);
}

// ── 정렬 검색 ──
export function searchWithSort(q: string, sort: string = "relevance") {
  const params = new URLSearchParams({ q, sort });
  return fetcher<SearchResultItem[]>(`/search?${params.toString()}`);
}

// ── 비교 ──
export function getCompare(titles: string[]) {
  return fetcher<CompareResponse>(`/compare?items=${titles.map(encodeURIComponent).join(",")}`);
}
